import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { JwtHelperService } from '@auth0/angular-jwt';
import { User, UserManager } from 'oidc-client';
import {BehaviorSubject, EMPTY, Observable, throwError as _throw} from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { UserModel } from 'app/models/user.model';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { MVLocalStorageService } from './mv-local-storage.service';
import moment from 'moment';
import { navigation } from 'app/navigation/navigation';
import { ConfigsLoaderService } from './config-loader.service';
import { AppRoutePaths } from 'app/enums/routes.enum';
import { PermissionType } from 'app/enums/permission.enum';
import { Item } from 'app/models/item.inferface';
import { SessionItems } from 'app/enums/constants.enum';


@Injectable({
  providedIn: 'root'
})
export class AuthService {
  public currentUser$: Observable<UserModel>;
  private apiEndPoint: string;
  private applicationId: number;
  private currentUserSubject: BehaviorSubject<UserModel>;
  private manager: UserManager;
  private identityToken: User = null;
  private availableTenants: Item[];


  constructor(
    private http: HttpClient,
    public jwtHelperService: JwtHelperService,
    private translate: TranslateService,
    private router: Router,
    private config: ConfigsLoaderService,
  ) {
    this.manager = new UserManager(this.config.auth);
    // Initialize the service
    this._init();
  }

  public get redirectUrl(): string {
    return MVLocalStorageService.getItem('__redirect__');
  }

  public set redirectUrl(value) {
    MVLocalStorageService.setItem('__redirect__', value);
  }

  get currentUser(): UserModel {
    return this.currentUserSubject.value;
  }
  // -----------------------------------------------------------------------------------------------------
  // @ Accessors
  // -----------------------------------------------------------------------------------------------------

  get currentUserName(): string {
    try {
      return this.currentUser?.userName;
    } catch {
      return 'N/A';
    }
  }

  tryToAuthorizeUser(): void {
    if (!this.currentUser) {
      // tslint:disable-next-line: no-string-literal
      this.prelogin()
        .subscribe();
    }
  }

  // tslint:disable-next-line: no-any
  getClaims(): any {
    console.log(this.identityToken)
    return this.identityToken.profile;
  }

  userHasPermission(permission: PermissionType): boolean {
    return this.currentUser.permissions.map(x => x.id).includes(permission as number);
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Private methods
  // -----------------------------------------------------------------------------------------------------

  // tslint:disable-next-line: no-any
  ping(ping: number): Observable<any> {
    return this.http
      .get(`${this.apiEndPoint}/authentication/ping/${ping}`);
  }

  prelogin() {
    const minutesFromUTC = moment().utcOffset();
    const bearer = this.getAuthorizationHeaderValue();
    const applicationId = ''+this.applicationId;
    let email = this.getClaims()[this.config.cloudEmailKey];
    if (!email){
      email = this.getClaims()["email"];
    }
    const headers = new HttpHeaders({ 'Authorization': bearer, applicationId });

    return this.http
      .post<Item[]>(`${this.apiEndPoint}/Authentication/PreAuthorize`, { email, minutesFromUTC }, { headers })
      .pipe(
        switchMap(items => {
          if (items?.length > 1) {
            this.availableTenants = items;
            return this.router.navigate(['tenant-selector']);
          } else {
            return this.login(items[0].id);
          }
        }),
      );
  }

  // -----------------------------------------------------------------------------------------------------
  // @ Public methods
  // -----------------------------------------------------------------------------------------------------
  // tslint:disable-next-line: no-any
  login(tenantId): Observable<any> {
    const minutesFromUTC = moment().utcOffset();
    const bearer = this.getAuthorizationHeaderValue();
    const applicationId = ''+this.applicationId;
    let email = this.getClaims()[this.config.cloudEmailKey];
    if (!email){
      email = this.getClaims()["email"];
    }
    const headers = new HttpHeaders({ 'Authorization': bearer, 'TenantId': tenantId, applicationId }); // 'StaticAuthorization' : key,
    return this.http
      .post<UserModel>(`${this.apiEndPoint}/Authentication/Authorize`, { email, minutesFromUTC }, { headers })
      .pipe(
        map(user => {
          MVLocalStorageService.setItem('current_user', JSON.stringify(user));
          this.currentUserSubject.next(user);

          if (this.redirectUrl && this.redirectUrl != '/') {
            if(this.redirectUrl == '/error'){
              this.redirectUrl = AppRoutePaths.HOME;
              this.router.navigate([this.redirectUrl]);
            }else{
              this.router.navigate([this.redirectUrl]);
            }
          } else {
            this.redirectUrl = AppRoutePaths.HOME;

            this.router.navigate([this.redirectUrl]);
          }
          return user;
        }),
        catchError(err => {
          return _throw(err);
        })
      );
  }

  logout(): void {
    if (this.currentUser) {
      const applicationId = this.applicationId;
      let headers = new HttpHeaders({
        Authorization: this.getAuthorizationHeaderValue()
      })

      this.http.post(`${this.apiEndPoint}/authentication/logout`, { headers: headers })
        .pipe(
          map(res => {
            this.clearUserData();
            this.manager.signoutRedirect();
          }),
          catchError(err => {
            this.clearUserData();
            if (err.errorcode) {
              return _throw(err.errorcode);
            }
            //this.router.navigate(['/unauthorized']);
            this.router.navigateByUrl(AppRoutePaths.FORBIDDEN);
          })
        ).subscribe();
    }
    else {
      this.clearUserData();
      this.manager.signoutRedirect();
    }
  }

  clearUserData() {
    // remove user from local storage to log user out
    MVLocalStorageService.removeItem('current_user');
    MVLocalStorageService.removeSessionItem('ticketlist_fitlerForm');
    MVLocalStorageService.removeSessionItem('ticketlist_sortOrder');
    MVLocalStorageService.removeSessionItem('ticketlist_sortBy');
    MVLocalStorageService.removeSessionItem('ticketlist_pageSize');
    MVLocalStorageService.removeItem('selected_tenant');

    MVLocalStorageService.removeSessionItem('ticketlist_fitlerForm');
    MVLocalStorageService.removeSessionItem('ticketlist_pageSize');
    MVLocalStorageService.removeSessionItem('ticketlist_sortOrder');
    MVLocalStorageService.removeSessionItem('ticketlist_sortBy');
    MVLocalStorageService.removeSessionItem('ppFileDirectoryRootFolderId');
    MVLocalStorageService.removeSessionItem('ppFileDirectoryCurrentFolderId');
    MVLocalStorageService.removeSessionItem('ppFileDirectoryCurrentParentFolderId');

    //clear dynamic-filters
    MVLocalStorageService.removeItem(SessionItems.alertDynamicFilterSideNav);
    MVLocalStorageService.removeSessionItem(SessionItems.alertDynamicFilterSideNav);
    MVLocalStorageService.removeItem(SessionItems.alertlist_dynamicFilters);
    MVLocalStorageService.removeItem(SessionItems.alertlist_dynamicFiltersApplied);
    MVLocalStorageService.removeItem(SessionItems.alertlist_dynamicFilterWorkflowSelected);
    MVLocalStorageService.removeSessionItem(SessionItems.alertlist_dynamicFilters);
    MVLocalStorageService.removeSessionItem(SessionItems.alertlist_dynamicFiltersApplied);
    MVLocalStorageService.removeSessionItem(SessionItems.alertlist_dynamicFilterWorkflowSelected);

    this.currentUserSubject.next(null);
  }

  isLoggedIn(): Promise<boolean> {
    return this.manager.getUser().then(user => {
      this.identityToken = user;

      return this.identityToken !== null && !this.identityToken.expired;
    });
  }

  isLoggedInAndAuthorized(): Promise<boolean> {
    return this.manager.getUser().then(user => {
      this.identityToken = user;
      return this.identityToken !== null && !this.identityToken.expired && !(!this.currentUser || !this.currentUser.permissions);
    });
  }

  isAuthorized(permissionId: string | string[]): boolean {
    if (!this.currentUser || !this.currentUser.permissions) {
      return false;
    }

    if (Array.isArray(permissionId)) {
      const permissions = permissionId.map(s => Number(s));
      return this.currentUser.permissions.some(p => permissions);
    } else {
      return this.currentUser.permissions.some(p => p.id === Number(permissionId));
    }
  }

  getAuthorizationHeaderValue(): string {
    if (this.identityToken.profile.tokenName === 'access_token') {
      return `${this.identityToken.token_type} ${this.identityToken.access_token}`;
    } else if (this.identityToken.profile.tokenName === 'id_token') {
      return `${this.identityToken.token_type} ${this.identityToken.id_token}`;
    } else if (this.identityToken.profile.tokenName === 'refresh_token') {
      return `${this.identityToken.token_type} ${this.identityToken.refresh_token}`;
    } else if (this.identityToken.profile.tokenName === undefined) {
        if (this.config.accessTokenType === 'access_token') {
            return `${this.identityToken.token_type} ${this.identityToken.access_token}`;
        } else {
            return `${this.identityToken.token_type} ${this.identityToken.id_token}`;
        }
    }
    return `${this.identityToken.token_type} ${this.identityToken.access_token}`;
  }

  setTokenPreferences(user) {
    if (user && user.token) {
      MVLocalStorageService.setItem('current_user', JSON.stringify(user));
      this.currentUserSubject.next(user);
    }
  }

  enforceLogin(pMessage): void {
    this.clearUserData();
    if (pMessage === 'ErrInvalidToken' || pMessage == 'ErrTheSessionIsOpenInOtherDevice') {
      const state = {
        message: pMessage
      };
      this.manager.signoutRedirect({ state });
    } else if (pMessage === 'ErrIncorrectEmail' || pMessage === 'ErrUserNotFound' || pMessage === 'DoNotHavePermissionsForThisApplication') {
      this.router.navigateByUrl(AppRoutePaths.FORBIDDEN);
    } else {
      this.router.navigate(['/logout-callback'], { queryParams: { errorMessage: 'La sesión expiró' } });
    }
  }
  handleLogoutCallback() {
    // Procesa la redirección de logout y extrae el estado si lo hay
    return this.manager.signoutRedirectCallback();
  }

  getUserByEmailForRegister(email: string): Observable<boolean> {
    return this.http.post<boolean>(`${this.apiEndPoint}/Authentication/GetUserByEmailForRegister/`, { email });
  }
  getUserByEmailForRecoveryPass(email: string): Observable<boolean> {
    return this.http.post<boolean>(`${this.apiEndPoint}/Authentication/GetUserByEmailForRecoveryPass/`, { email })
      .pipe(
        map((response) => {
          return response;
        }),
        catchError(err => {
          if (err) {
            return _throw(err);
          }
          return _throw(this.translate.instant('errors.ErrUnexpected'));
        })
      );
  }

  startAuthentication(): Promise<void> {
    return this.manager.signinRedirect();
  }

  completeAuthentication(): Promise<void> {
    return this.manager.signinRedirectCallback().then(user => {
      this.identityToken = user;
    });
  }
  checkFirstAllowedMenuItem() {
    let url = null;
    navigation.forEach(item => {
      if (!url) {
        if (this.isAuthorized(item.permission)) {
          url = item.url;
        }
      }
    });

    if (url) {
      this.router.navigate([url]);
    } else {
      this.router.navigate(['/forbidden']);
    }
  }

  changeDefaultTenant(tenantId: number) {
    return this.http.put(`${this.apiEndPoint}/User/UpdateDefaultTenant/${tenantId}`, null)
      .subscribe();
  }
  private _init(): void {
    // Set initial value
    this.apiEndPoint = this.config.apiUrl;
    this.applicationId = this.config.applicationId;

    this.currentUserSubject = new BehaviorSubject<UserModel>(JSON.parse(MVLocalStorageService.getItem('current_user')));
    this.currentUser$ = this.currentUserSubject.asObservable();
  }

  getAvailableTenants() {
    return this.availableTenants;
  }
}
